import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import DOMPurify from "dompurify";
import { Link } from "react-router-dom";

import {
  FiArrowRight,
  FiGlobe,
  FiMail,
  FiMap,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
} from "react-icons/fi";

import {
  FaFacebookF,
  FaInstagram,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import CommerceAdaptiveImage from "./CommerceAdaptiveImage";
import CommerceContactLink from "./CommerceContactLink";
import CommerceMapPanel from "./CommerceMapPanel";

import { cn } from "../utils/commercesUtils";

/* -------------------------------------------------------------------------- */
/*                                  Config                                    */
/* -------------------------------------------------------------------------- */

const DESCRIPTION_MAX_HEIGHT = 155;

/* -------------------------------------------------------------------------- */
/*                         Détection vraie image                              */
/* -------------------------------------------------------------------------- */

function hasRealImage(value) {
  const src = String(value || "").trim();

  if (!src) return false;

  const lower = src.toLowerCase();

  const placeholderPatterns = [
    "placeholder",
    "no-image",
    "no_image",
    "noimage",
    "pas-image",
    "pas_d_image",
    "pas-d-image",
    "default-image",
    "image-default",
  ];

  return !placeholderPatterns.some((pattern) =>
    lower.includes(pattern),
  );
}

/* -------------------------------------------------------------------------- */
/*                        Description HTML enrichie                           */
/* -------------------------------------------------------------------------- */

function CommerceRichDescription({ value }) {
  const contentRef = useRef(null);

  const [overflowing, setOverflowing] =
    useState(false);

  const safeHtml = useMemo(() => {
    const html = String(value || "").trim();

    if (!html) return "";

    return DOMPurify.sanitize(html, {
      USE_PROFILES: {
        html: true,
      },

      /*
       * On supprime les styles éventuellement
       * générés par l'éditeur de texte enrichi.
       *
       * Le thème du site gère les couleurs.
       */
      FORBID_ATTR: [
        "style",
        "class",
        "color",
        "bgcolor",
        "face",
        "size",
      ],

      FORBID_TAGS: [
        "font",
      ],

      ADD_ATTR: [
        "target",
        "rel",
      ],
    });
  }, [value]);

  /* ------------------------------------------------------------------------ */
  /*                    Détection réelle du débordement                       */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const element =
      contentRef.current;

    if (!element || !safeHtml) {
      setOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      const naturalHeight =
        element.scrollHeight;

      setOverflowing(
        naturalHeight >
          DESCRIPTION_MAX_HEIGHT + 4,
      );
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(
            checkOverflow,
          )
        : null;

    resizeObserver?.observe(
      element,
    );

    window.addEventListener(
      "resize",
      checkOverflow,
    );

    return () => {
      resizeObserver?.disconnect();

      window.removeEventListener(
        "resize",
        checkOverflow,
      );
    };
  }, [safeHtml]);

  if (!safeHtml) {
    return null;
  }

  return (
    <div className="relative">
      <div
        className="overflow-hidden"
        style={{
          maxHeight: `${DESCRIPTION_MAX_HEIGHT}px`,
        }}
      >
        <div
          ref={contentRef}
          className="
            text-sm
            leading-7
            !text-slate-700
            dark:!text-slate-300

            [&_*]:!text-inherit

            [&_p]:mb-2.5
            [&_p]:!text-slate-700
            dark:[&_p]:!text-slate-300
            [&_p:last-child]:mb-0

            [&_div]:!text-slate-700
            dark:[&_div]:!text-slate-300

            [&_span]:!text-slate-700
            dark:[&_span]:!text-slate-300

            [&_strong]:font-bold
            [&_strong]:!text-slate-950
            dark:[&_strong]:!text-white

            [&_b]:font-bold
            [&_b]:!text-slate-950
            dark:[&_b]:!text-white

            [&_em]:italic
            [&_i]:italic

            [&_u]:underline
            [&_u]:underline-offset-2

            [&_h1]:mb-2
            [&_h1]:mt-3
            [&_h1]:text-lg
            [&_h1]:font-black
            [&_h1]:leading-tight
            [&_h1]:!text-slate-950
            dark:[&_h1]:!text-white

            [&_h2]:mb-2
            [&_h2]:mt-3
            [&_h2]:text-base
            [&_h2]:font-black
            [&_h2]:leading-tight
            [&_h2]:!text-slate-950
            dark:[&_h2]:!text-white

            [&_h3]:mb-1.5
            [&_h3]:mt-2
            [&_h3]:font-bold
            [&_h3]:!text-slate-900
            dark:[&_h3]:!text-slate-100

            [&_ul]:my-2
            [&_ul]:list-disc
            [&_ul]:space-y-1
            [&_ul]:pl-5
            [&_ul]:!text-slate-700
            dark:[&_ul]:!text-slate-300

            [&_ol]:my-2
            [&_ol]:list-decimal
            [&_ol]:space-y-1
            [&_ol]:pl-5
            [&_ol]:!text-slate-700
            dark:[&_ol]:!text-slate-300

            [&_li]:!text-slate-700
            dark:[&_li]:!text-slate-300

            [&_blockquote]:my-3
            [&_blockquote]:rounded-r-xl
            [&_blockquote]:border-l-4
            [&_blockquote]:border-sky-300
            [&_blockquote]:bg-sky-50
            [&_blockquote]:px-3
            [&_blockquote]:py-2
            [&_blockquote]:italic
            [&_blockquote]:!text-slate-600

            dark:[&_blockquote]:border-sky-700
            dark:[&_blockquote]:bg-sky-950/30
            dark:[&_blockquote]:!text-slate-300

            [&_a]:font-bold
            [&_a]:!text-[#1277B9]
            [&_a]:underline
            [&_a]:decoration-[#1277B9]/30
            [&_a]:underline-offset-2
            [&_a]:transition
            [&_a:hover]:decoration-[#1277B9]

            dark:[&_a]:!text-sky-400

            [&_hr]:my-3
            [&_hr]:border-slate-200
            dark:[&_hr]:border-slate-800
          "
          dangerouslySetInnerHTML={{
            __html: safeHtml,
          }}
        />
      </div>

      {/* IMPORTANT :
          le dégradé n'existe QUE si le contenu dépasse réellement */}
      {overflowing ? (
        <div
          className="
            pointer-events-none
            absolute
            inset-x-0
            bottom-0
            h-12

            bg-gradient-to-t
            from-white
            via-white/95
            to-transparent

            dark:from-slate-950
            dark:via-slate-950/95
          "
        />
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Header avec photo                                */
/* -------------------------------------------------------------------------- */

function CommercePhotoHeader({
  commerce,
}) {
  return (
    <div className="relative h-64 overflow-hidden">
      <CommerceAdaptiveImage
        src={commerce.image}
        alt={commerce.title}
        className="h-full"
      />

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          z-20
          bg-gradient-to-t
          from-slate-950/80
          via-slate-950/10
          to-transparent
        "
      />

      {commerce.logo ? (
        <div
          className="
            absolute
            left-4
            top-4
            z-30
            h-14
            w-14
            overflow-hidden
            rounded-2xl
            border
            border-white/80
            bg-white
            shadow-lg
          "
        >
          <img
            src={commerce.logo}
            alt={`Logo ${commerce.title}`}
            className="h-full w-full object-contain p-1"
          />
        </div>
      ) : null}

      <div className="absolute bottom-4 left-4 right-4 z-30">
        {commerce.category ? (
          <span
            className="
              inline-flex
              rounded-full
              bg-white/95
              px-3
              py-1
              text-xs
              font-bold
              text-[#1277B9]
              shadow-sm
              backdrop-blur
            "
          >
            {commerce.category}
          </span>
        ) : null}

        <h2 className="mt-2 line-clamp-2 text-2xl font-black leading-tight text-white drop-shadow-md">
          {commerce.title}
        </h2>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Header sans photo                                */
/* -------------------------------------------------------------------------- */

function CommerceNoPhotoHeader({
  commerce,
}) {
  return (
    <div
      className="
        relative
        flex
        h-64
        overflow-hidden
        border-b
        border-slate-200

        bg-gradient-to-br
        from-[#1277B9]
        via-[#116fac]
        to-[#0d5687]

        dark:border-slate-800
        dark:from-sky-900
        dark:via-slate-900
        dark:to-slate-950
      "
    >
      {/* Décoration CSS uniquement */}

      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full border border-white/10" />

      <div className="pointer-events-none absolute -right-5 -top-10 h-44 w-44 rounded-full border border-white/10" />

      <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-white/5" />

      <div className="relative z-10 flex w-full flex-col justify-between p-5">
        {/* Logo ou pictogramme */}

        <div>
          {commerce.logo ? (
            <div
              className="
                flex
                h-16
                w-16
                items-center
                justify-center
                overflow-hidden
                rounded-2xl
                border
                border-white/50
                bg-white
                shadow-lg
              "
            >
              <img
                src={commerce.logo}
                alt={`Logo ${commerce.title}`}
                className="h-full w-full object-contain p-1.5"
              />
            </div>
          ) : (
            <div
              className="
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-2xl
                border
                border-white/20
                bg-white/10
                text-white
                backdrop-blur
              "
            >
              <FiShoppingBag className="h-6 w-6" />
            </div>
          )}
        </div>

        {/* Identification */}

        <div>
          {commerce.category ? (
            <span
              className="
                inline-flex
                rounded-full
                bg-white
                px-3
                py-1
                text-xs
                font-bold
                text-[#1277B9]
                shadow-sm
              "
            >
              {commerce.category}
            </span>
          ) : null}

          <h2 className="mt-3 line-clamp-2 text-2xl font-black leading-tight text-white sm:text-[1.65rem]">
            {commerce.title}
          </h2>

          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/65">
            Commerce de la commune
          </p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            CommerceCard                                    */
/* -------------------------------------------------------------------------- */

export default function CommerceCard({
  commerce,
  isMapOpen = false,
  onToggleMap,
  delay = 0,
}) {
  const showPhoto =
    hasRealImage(commerce?.image);

  const richDescription =
    commerce?.descriptionHtml ||
    commerce?.description ||
    commerce?.descriptionPreview ||
    "";

  return (
    <Reveal delay={delay}>
      <article
        className="
          group
          flex
          h-full
          flex-col
          overflow-hidden
          rounded-[2rem]
          border
          border-slate-200
          bg-white
          shadow-sm
          transition
          duration-300

          hover:-translate-y-1
          hover:border-[#1277B9]/50
          hover:shadow-xl

          dark:border-slate-800
          dark:bg-slate-950
        "
      >
        {showPhoto ? (
          <CommercePhotoHeader
            commerce={commerce}
          />
        ) : (
          <CommerceNoPhotoHeader
            commerce={commerce}
          />
        )}

        <div className="flex flex-1 flex-col p-5">
          {/* Description enrichie */}

          {richDescription ? (
            <CommerceRichDescription
              value={richDescription}
            />
          ) : null}

          {/* Adresse */}

          {commerce.address ? (
            <div
              className={cn(
                "flex items-start gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400",
                richDescription
                  ? "mt-5"
                  : "",
              )}
            >
              <FiMapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />

              {commerce.mapsHref ? (
                <a
                  href={commerce.mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="transition hover:text-[#1277B9] hover:underline"
                >
                  {commerce.address}
                </a>
              ) : (
                <span>
                  {commerce.address}
                </span>
              )}
            </div>
          ) : null}

          {/* Contacts */}

          <div className="mt-5 flex flex-wrap gap-2">
            <CommerceContactLink
              href={commerce.phoneHref}
              icon={FiPhone}
              label={
                commerce.phone ||
                "Téléphoner"
              }
            />

            <CommerceContactLink
              href={
                commerce.mail
                  ? `mailto:${commerce.mail}`
                  : ""
              }
              icon={FiMail}
              label="Email"
              variant={
                commerce.mail
                  ? "primary"
                  : "default"
              }
            />

            <CommerceContactLink
              href={commerce.websiteUrl}
              icon={FiGlobe}
              label="Site"
              external
            />

            <CommerceContactLink
              href={commerce.facebookUrl}
              icon={FaFacebookF}
              label="Facebook"
              external
            />

            <CommerceContactLink
              href={commerce.instagramUrl}
              icon={FaInstagram}
              label="Instagram"
              external
            />
          </div>

          {/* Actions */}

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <Link
              to={`/commerces/${commerce.slug}`}
              className="
                inline-flex
                min-h-11
                flex-1
                items-center
                justify-center
                gap-2
                rounded-2xl
                bg-[#1277B9]
                px-4
                py-2
                text-sm
                font-bold
                text-white
                shadow-sm
                transition
                hover:bg-[#0f659d]
                focus:outline-none
                focus:ring-2
                focus:ring-[#1277B9]/40
                sm:flex-none
              "
            >
              En savoir plus

              <FiArrowRight className="h-4 w-4" />
            </Link>

            {commerce.hasMap ? (
              <button
                type="button"
                onClick={onToggleMap}
                className={cn(
                  "inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border px-4 py-2 text-sm font-bold transition sm:flex-none",

                  isMapOpen
                    ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                    : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800",
                )}
              >
                <FiMap className="h-4 w-4" />

                {isMapOpen
                  ? "Masquer la carte"
                  : "Voir la carte"}
              </button>
            ) : null}
          </div>

          {isMapOpen ? (
            <CommerceMapPanel
              commerce={commerce}
            />
          ) : null}
        </div>
      </article>
    </Reveal>
  );
}